import type { Layer, TagsManager } from 'dxf-writer'
import DatabaseObject from 'dxf-writer/src/DatabaseObject.js'
import type Handle from 'dxf-writer/src/Handle.js'
import { DimStyleName } from './constant'

/**
 * DXF 对齐标注（DIMENSION）实体，用于导出两点间线性对齐尺寸。
 */
export default class AlignDimension extends DatabaseObject {
  /** 所属图层 */
  layer!: Layer
  /** 线型名称，默认跟随图层 */
  lineType: string = 'ByLayer'
  /** 线宽，单位百分之毫米；-1 表示跟随图层 */
  lineWidth: number = -1
  /** 真彩色 RGB 整数，undefined 表示不写入 */
  trueColor: number | undefined

  constructor(
    public p1: { x: number; y: number },
    public p2: { x: number; y: number },
    public offsetPoint: { x: number; y: number },
    public textPosition: { x: number; y: number },
    public text: string,
    public textHeight: number = 2.5,
    public textGap: number = 0.625,
    public arrowSize: number = 2.5,
    public hasUserDefinedTextPosition: boolean = false,
    public lineTypeHandle?: Handle,
    public blockName?: string
  ) {
    super('AcDbEntity')
  }

  /**
   * 写入 DIMENSION 组码序列。
   *
   * @param manager - DXF 标签写入器
   */
  tags(manager: TagsManager) {
    manager.push(0, 'DIMENSION')
    super.tags(manager)
    manager.push(8, this.layer.name)
    manager.push(6, this.lineType)
    manager.push(62, 256)
    manager.push(370, this.lineWidth)
    if (this.trueColor !== undefined) {
      manager.push(420, this.trueColor)
    }

    manager.push(100, 'AcDbDimension')
    manager.push(280, 0)
    if (this.blockName) {
      manager.push(2, this.blockName)
    }
    manager.point(this.offsetPoint.x, this.offsetPoint.y)
    manager.push(70, 1 | (this.blockName ? 32 : 0) | (this.hasUserDefinedTextPosition ? 128 : 0))
    manager.push(11, this.textPosition.x)
    manager.push(21, this.textPosition.y)
    manager.push(31, 0)
    manager.push(71, 5)
    manager.push(1, this.text)
    manager.push(3, DimStyleName) // dimension style name

    manager.push(100, 'AcDbAlignedDimension')
    manager.push(13, this.p1.x)
    manager.push(23, this.p1.y)
    manager.push(14, this.p2.x)
    manager.push(24, this.p2.y)
    // 扩展数据
    manager.push(1001, 'ACAD')
    manager.push(1000, 'DSTYLE') // dimension style
    manager.push(1002, '{')
    this.pushShortOverride(manager, 73, 0) // DIMTIH 文字在尺寸界线内的位置
    this.pushShortOverride(manager, 77, 1) // DIMTAD 垂直放置文字
    this.pushShortOverride(manager, 174, 0) // DIMTIX 文字界内对齐
    this.pushRealOverride(manager, 140, this.textHeight) // DIMTXT
    this.pushRealOverride(manager, 147, this.textGap) // DIMGAP
    this.pushRealOverride(manager, 41, this.arrowSize) // DIMASZ
    if (this.lineTypeHandle) {
      this.pushHandleOverride(manager, 343, this.lineTypeHandle) // DIMLTYPE
      this.pushHandleOverride(manager, 344, this.lineTypeHandle)
      this.pushHandleOverride(manager, 345, this.lineTypeHandle)
    }
    manager.push(1002, '}')
  }

  private pushShortOverride(manager: TagsManager, code: number, value: number) {
    manager.push(1070, code)
    manager.push(1070, value)
  }

  private pushRealOverride(manager: TagsManager, code: number, value: number) {
    manager.push(1070, code)
    manager.push(1040, value)
  }

  private pushHandleOverride(manager: TagsManager, code: number, handle: Handle) {
    manager.push(1070, code)
    manager.push(1005, handle)
  }
}
